import { useCallback, useMemo } from 'react';
import { Step } from 'react-joyride';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';

import Onboarded from 'assets/onboarded.png';

import { WALKTHROUGH_CHECK } from 'lib/constants';
import useAuthStore from 'store/auth';
import { useMediaPredicate } from 'react-media-hook';

export default function useWalkthrough() {
  const { t } = useTranslation();
  const adminProfileId = useAuthStore((store) => store.adminProfileId);

  const [cookies, setCookie] = useCookies();

  const updateStep = (step: WALKTHROUGH_CHECK) => {
    const updatedIds = !cookies[step]
      ? JSON.stringify([adminProfileId])
      : JSON.stringify([...new Set([...cookies[step], adminProfileId])]);
    setCookie(step, updatedIds);
  };

  const deleteStep = (step: WALKTHROUGH_CHECK) => {
    const updatedIds =
      cookies[step]?.length &&
      JSON.stringify(
        cookies[step].filter((id: string) => id !== adminProfileId)
      );
    setCookie(step, updatedIds);
  };

  const checkWalk = useCallback(
    (step: WALKTHROUGH_CHECK) =>
      Boolean(cookies[step] && cookies[step].includes(adminProfileId)),
    [cookies, adminProfileId]
  );

  const isMediaMobile = useMediaPredicate('(max-width: 640px)');

  const walkthroughSteps = useMemo(
    () =>
      ({
        dashboard: [
          {
            target: 'body',
            content: (
              <div className='space-y-3 sm:space-y-6'>
                <div className='mx-auto size-52 sm:size-60'>
                  <img
                    src={Onboarded}
                    alt='onbaorded'
                    className='object-contain w-full h-full'
                  />
                </div>
                <h2 className='text-lg font-semibold sm:text-2xl'>
                  {t('welcome_to_medsicheck')}
                </h2>
                <p className='py-2 text-sm font-medium leading-snug border sm:text-base sm:py-6 sm:mx-4 border-primary rounded-xl'>
                  {t('future_of_health_checks')},
                  <br />
                  {t('simple_scan_for_insights')}
                </p>
              </div>
            ),
            locale: {
              next: <p>{t('get_started')}</p>,
              skip: <p>{t('no_tour')}</p>,
            },
            disableBeacon: true,
            placement: 'center',
            styles: {
              options: {
                width: isMediaMobile ? '300px' : '500px',
              },
              ...(!isMediaMobile && {
                buttonNext: {
                  borderRadius: '50px',
                  marginBottom: '10px',
                  transform: 'translateX(-80%)',
                  paddingTop: '10px 20px',
                },
                buttonSkip: {
                  transform: 'translateX(50%)',
                  textDecoration: 'underline',
                  textUnderlineOffset: '5px',
                  marginBottom: '10px',
                },
              }),
            },
          },
          {
            target: '#dashboard',
            content: t('tour_sidebar_dashboard'),
            placement: 'right',
          },
          {
            target: '#face-scan',
            content: t('tour_sidebar_face_scan'),
            placement: 'right',
          },
          {
            target: '#ai-report',
            content: t('tour_sidebar_ai_report'),
            placement: 'right',
          },
          {
            target: '#interpret-report',
            content: t('tour_sidebar_interpret_report'),
            placement: 'right',
          },
          // {
          //   target: '#miscellaneous',
          //   content: 'This section is for miscellaneous files you can see all those details here',
          //   placement: 'right',
          // },
          {
            target: '#profile',
            content: t('tour_sidebar_profile'),
            placement: 'right',
          },
          {
            target: '#settings',
            content: t('tour_sidebar_settings'),
            placement: 'right',
          },
          {
            target: '#logout',
            content: t('tour_sidebar_logout'),
            placement: 'bottom',
          },
        ],
        faceScan: [
          {
            target: '#start-scan',
            content: t('tour_facescan_start'),
            placement: 'right',
            disableBeacon: true,
          },
          {
            target: '#previous-scans',
            content: t('tour_facescan_previous_scans'),
            placement: 'right',
          },
          // {
          //   target: '#sort-share',
          //   content:
          //     'You can share, download the scan reports and you can sort the listings',
          //   placement: 'right',
          // },
          // {
          //   target: '#previous-scan-table',
          //   content:
          //     'This is list of previous scans, you can see the detailed report by clicking it.',
          //   placement: 'right',
          // },
          {
            target: '#vital-signs',
            content: t('tour_facescan_vital_signs'),
            placement: 'top-start',
          },
        ],
      } satisfies Record<string, Array<Step>>),
    []
  );

  return { updateStep, checkWalk, deleteStep, walkthroughSteps };
}
